import { Directive, ElementRef, Input, AfterViewInit, OnDestroy } from '@angular/core';
import { KeyboardNavigationHelper } from './keyboard-navigation.helper';

@Directive({
  selector: '[appActionContainer]'
})
export class ActionContainerDirective implements AfterViewInit, OnDestroy {

  @Input() rowIndex: number = 0;
  @Input() tableId: string = '';

  private focusableElements: HTMLElement[] = [];
  private keydownListeners: ((event: KeyboardEvent) => void)[] = [];

  constructor(private readonly el: ElementRef) {}

  ngAfterViewInit() {
    // Small delay to ensure DOM is fully rendered
    setTimeout(() => {
      this.setupFocusableElements();
    }, 50);
  }

  ngOnDestroy() {
    // Clean up event listeners
    this.focusableElements.forEach((element, index) => {
      if (this.keydownListeners[index]) {
        element.removeEventListener('keydown', this.keydownListeners[index]);
      }
    });
  }

  private setupFocusableElements() {
    // Find all focusable elements (buttons, links, inputs with tabindex >= 0)
    const selector = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.focusableElements = Array.from(
      this.el.nativeElement.querySelectorAll(selector)
    ) as HTMLElement[];

    // Add data attributes and keyboard handlers to each element
    this.focusableElements.forEach((element, index) => {
      // Set data attributes for DOM queries
      element.setAttribute('data-action-row', this.rowIndex.toString());
      element.setAttribute('data-action-index', index.toString());
      element.setAttribute('data-table-id', this.tableId);

      // Create and store keydown listener
      const listener = (event: KeyboardEvent) => {
        this.handleKeydown(event, index);
      };
      this.keydownListeners[index] = listener;
      element.addEventListener('keydown', listener);
    });
  }

  private handleKeydown(event: KeyboardEvent, elementIndex: number) {
    const totalElements = this.focusableElements.length;

    switch (event.key) {
      case 'ArrowRight':
        this.handleArrowRight(event, elementIndex, totalElements);
        break;
      case 'ArrowLeft':
        this.handleArrowLeft(event, elementIndex);
        break;
      case 'ArrowUp': {
        this.handleArrowUpDown(event, elementIndex, 'up');
        break;
      }
      case 'ArrowDown': {
        this.handleArrowUpDown(event, elementIndex, 'down');
        break;
      }
    }
  }

  private handleArrowRight(event: KeyboardEvent, elementIndex: number, totalElements: number) {
    if (elementIndex === totalElements - 1) {
      // Last action button - go to first cell of same row
      this.navigateToCell(this.rowIndex, 0);
    } else {
      // Move to next action button in same row
      const nextElement = this.focusableElements[elementIndex + 1];
      if (nextElement) {
        nextElement.focus();
        this.scrollRowIntoView(this.rowIndex);
      }
    }
    event.preventDefault();
    event.stopPropagation();
  }

  private handleArrowLeft(event: KeyboardEvent, elementIndex: number) {
    if (elementIndex === 0) {
      // First action button - go to checkbox or last cell of previous row
      this.navigateToCheckboxOrPreviousCell();
    } else {
      // Move to previous action button in same row
      const prevElement = this.focusableElements[elementIndex - 1];
      if (prevElement) {
        prevElement.focus();
        this.scrollRowIntoView(this.rowIndex);
      }
    }
    event.preventDefault();
    event.stopPropagation();
  }

  private handleArrowUpDown(event: KeyboardEvent, elementIndex: number, direction: 'up' | 'down') {
    // Go to same position action in previous/next row
    const targetRow = this.findNextVisibleRow(direction);
    if (targetRow !== null) {
      this.navigateToActionElement(targetRow, elementIndex);
    }
    event.preventDefault();
    event.stopPropagation();
  }

  // Navigate to a specific cell
  private navigateToCell(row: number, col: number) {
    const selector = `[data-table-id="${this.tableId}"] [data-row-index="${row}"][data-col-index="${col}"]`;
    const cell = document.querySelector(selector) as HTMLElement;
    if (cell) {
      cell.focus();
      cell.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }

  // Navigate to checkbox of same row, or last cell of previous row if no checkbox
  private navigateToCheckboxOrPreviousCell() {
    // Try to find checkbox in same row
    const checkboxSelector = `[data-table-id="${this.tableId}"] [data-checkbox-row="${this.rowIndex}"]`;
    const checkbox = document.querySelector(checkboxSelector) as HTMLElement;

    if (checkbox) {
      checkbox.focus();
      this.scrollRowIntoView(this.rowIndex);
    } else {
      // No checkbox, try to go to last cell of previous row
      // First find how many columns exist by checking current row
      const cellsInRow = document.querySelectorAll(`[data-table-id="${this.tableId}"] [data-row-index="${this.rowIndex}"]`);
      if (cellsInRow.length > 0) {
        // Find the highest column index
        let maxCol = -1;
        cellsInRow.forEach(cell => {
          const colIndex = parseInt(cell.getAttribute('data-col-index') || '-1');
          if (colIndex > maxCol) maxCol = colIndex;
        });

        if (maxCol >= 0) {
          // Navigate to last cell of previous row
          this.navigateToCell(this.rowIndex - 1, maxCol);
        }
      }
    }
  }

  // Find the next visible row index in the specified direction
  // Returns null if at boundary or no rows found
  private findNextVisibleRow(direction: 'up' | 'down'): number | string | null {
    return KeyboardNavigationHelper.findNextVisibleRow(this.rowIndex, this.tableId, direction);
  }

  // Navigate to action button at specific row and index
  private navigateToActionElement(row: number | string, index: number) {
    let selector = `[data-table-id="${this.tableId}"] [data-action-row="${row}"][data-action-index="${index}"]`;
    let element = document.querySelector(selector) as HTMLElement;

    // If target button doesn't exist at the same index, fall back to last button in target row
    if (!element) {
      // Find all action buttons in target row
      const allButtonsInRow = document.querySelectorAll(
        `[data-table-id="${this.tableId}"] [data-action-row="${row}"]`
      ) as NodeListOf<HTMLElement>;
      
      if (allButtonsInRow.length > 0) {
        // Get the last button in the row
        element = allButtonsInRow[allButtonsInRow.length - 1];
      }
    }

    if (element) {
      element.focus();
      // For frozen columns, scroll the parent row instead of the element itself
      this.scrollRowIntoView(row);
    }
  }

  // Scroll a row into view (works for frozen columns)
  private scrollRowIntoView(row: number | string) {
    // Find any cell in the target row (use a data cell, not frozen column)
    const cellSelector = `[data-table-id="${this.tableId}"] [data-row-index="${row}"][data-col-index="0"]`;
    const cell = document.querySelector(cellSelector) as HTMLElement;
    
    if (cell) {
      cell.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }
}
